import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, RefreshCw, Send, AlertCircle, PauseCircle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Audit log of every email the system attempted to send for this proposal —
 * timestamp, recipient, subject, delivery status, and a Resend action that
 * replays the original payload through SMTP.
 *
 * Backend: GET /api/proposals/{id}/email-log and POST .../resend/{log_id}
 */
const STATUS_META = {
  sent: { label: 'Delivered', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
  failed: { label: 'Failed', color: '#dc2626', bg: '#fee2e2', icon: AlertCircle },
  paused: { label: 'Paused (kill switch)', color: '#b45309', bg: '#fef3c7', icon: PauseCircle },
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

const EmailActivityPanel = ({ proposalId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [resendingId, setResendingId] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!proposalId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`/proposals/${proposalId}/email-log`);
      setLogs(data.logs || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load email activity');
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleResend = async (log) => {
    if (resendingId) return;
    const confirmed = window.confirm(`Resend "${log.label || log.email_type}" to ${log.to_email}?`);
    if (!confirmed) return;
    setResendingId(log.id);
    try {
      const { data } = await axios.post(`/proposals/${proposalId}/email-log/${log.id}/resend`);
      if (data.success) {
        toast.success(`Resent ${log.label || log.email_type} → ${data.to}`);
      } else {
        toast.warning(`Send returned no success — check kill switch / SMTP credentials.`);
      }
      fetchLogs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div
      style={{
        marginBottom: 24,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        overflow: 'hidden',
      }}
      data-testid="email-activity-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="email-activity-toggle"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          background: '#f8fafc',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <Mail size={18} color="#0ea5e9" />
          <span style={{ fontWeight: 600, color: '#0f172a' }}>Email Activity</span>
          <span
            style={{
              fontSize: 12,
              padding: '2px 8px',
              borderRadius: 999,
              background: '#e0f2fe',
              color: '#0369a1',
              fontWeight: 600,
            }}
            data-testid="email-activity-count"
          >
            {logs.length}
          </span>
        </div>
        <span
          onClick={(e) => { e.stopPropagation(); fetchLogs(); }}
          role="button"
          tabIndex={0}
          data-testid="email-activity-refresh"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            color: '#64748b',
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </span>
      </button>

      {expanded && (
        <div>
          {loading && logs.length === 0 && (
            <div style={{ padding: 18, color: '#64748b', fontSize: 13 }}>Loading…</div>
          )}
          {!loading && logs.length === 0 && (
            <div style={{ padding: 18, color: '#64748b', fontSize: 13 }} data-testid="email-activity-empty">
              No emails have been sent for this proposal yet. They&apos;ll appear here automatically as soon as you click <strong>Send Email</strong> or trigger a follow-up.
            </div>
          )}
          {logs.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="email-activity-list">
              {logs.map((log) => {
                const meta = STATUS_META[log.status] || { label: log.status, color: '#475569', bg: '#f1f5f9', icon: Mail };
                const Icon = meta.icon;
                return (
                  <li
                    key={log.id}
                    data-testid={`email-log-${log.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '14px 18px',
                      borderTop: '1px solid #f1f5f9',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                          {log.label || log.email_type}
                        </span>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: meta.bg,
                            color: meta.color,
                          }}
                          data-testid={`email-log-status-${log.id}`}
                        >
                          <Icon size={12} /> {meta.label}
                        </span>
                      </div>
                      <div style={{ marginTop: 4, color: '#475569', fontSize: 13, wordBreak: 'break-word' }}>
                        <span style={{ color: '#0f172a' }}>To:</span> {log.to_email}
                        {log.cc_emails && log.cc_emails.length > 0 && (
                          <> · <span style={{ color: '#0f172a' }}>Cc:</span> {log.cc_emails.join(', ')}</>
                        )}
                      </div>
                      {log.subject && (
                        <div style={{ marginTop: 2, color: '#475569', fontSize: 13, fontStyle: 'italic' }}>
                          &ldquo;{log.subject}&rdquo;
                        </div>
                      )}
                      <div style={{ marginTop: 4, color: '#94a3b8', fontSize: 12 }}>
                        {formatTime(log.created_at)}
                        {log.triggered_by && <> · {log.triggered_by}</>}
                      </div>
                      {log.error && (
                        <div
                          style={{
                            marginTop: 6,
                            padding: '6px 10px',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            color: '#991b1b',
                            fontSize: 12,
                            borderRadius: 6,
                          }}
                          data-testid={`email-log-error-${log.id}`}
                        >
                          {log.error}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleResend(log)}
                      disabled={resendingId === log.id}
                      data-testid={`email-log-resend-${log.id}`}
                      title={`Re-send this ${log.label || log.email_type}`}
                    >
                      <Send size={14} style={{ marginRight: 4 }} />
                      {resendingId === log.id ? 'Sending…' : 'Resend'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailActivityPanel;
